import React, { useState, useRef, useEffect } from "react";
import styled, { css, keyframes } from "styled-components";
import { Modal } from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import CloseIcon from "@material-ui/icons/Close";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import VisibilityIcon from "@material-ui/icons/Visibility";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import SendIcon from "@material-ui/icons/Send";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import { TIFFViewer } from "react-tiff";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import FormField from "../../common/FormField/FormField";
import { PrimaryCTAButton } from "../../common/Buttons";
import theme from "../../../assets/theme";

const BRAND = "#00838c";
const BRAND_DARK = "#00606a";
const INK = "#293461";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const extColor = {
  pdf: "#dc2626",
  docx: "#2563eb",
  doc: "#2563eb",
  xlsx: "#16a34a",
  xls: "#16a34a",
  csv: "#0891b2",
  tif: "#9333ea",
  tiff: "#9333ea",
  pptx: "#ea580c",
  ppt: "#ea580c",
  txt: "#64748b",
  json: "#ca8a04",
  xml: "#0369a1",
};

const getFileKind = (ext) => {
  if (ext === "pdf") return "PDF document";
  if (["docx", "doc"].includes(ext)) return "Word document";
  if (["pptx", "ppt"].includes(ext)) return "Presentation";
  if (["xlsx", "xls", "csv"].includes(ext)) return "Spreadsheet";
  if (["tif", "tiff"].includes(ext)) return "TIFF image";
  if (["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext))
    return "Image";
  if (["mp4", "mkv", "mov", "webm"].includes(ext)) return "Video";
  if (["mp3", "wav", "ogg", "m4a"].includes(ext)) return "Audio";
  return ext ? `${ext.toUpperCase()} file` : "Document";
};

const Skeleton = ({ label }) => (
  <SkeletonWrap>
    <Spinner />
    <SkBars>
      {[82, 60, 95, 50, 74, 40, 88].map((w, i) => (
        <SkBar
          key={i}
          style={{ width: `${w}%`, animationDelay: `${i * 0.12}s` }}
        />
      ))}
    </SkBars>
    <SkLabel>{label || "Loading…"}</SkLabel>
  </SkeletonWrap>
);

const IframeViewer = ({ src, fallbackSrc, label }) => {
  const [loading, setLoading] = useState(true);
  const [currentSrc, setCurrentSrc] = useState(src);
  const [triedFallback, setTriedFallback] = useState(false);
  const timerRef = useRef(null);

  const startTimer = (nextSrc) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (fallbackSrc && !triedFallback) {
        setTriedFallback(true);
        setCurrentSrc(fallbackSrc);
        setLoading(true);
        startTimer(fallbackSrc);
      } else {
        setLoading(false);
      }
    }, 12000);
  };

  useEffect(() => {
    startTimer(currentSrc);
    return () => clearTimeout(timerRef.current); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSrc]);

  const onLoad = () => {
    clearTimeout(timerRef.current);
    setLoading(false);
  };

  return (
    <IframeWrap>
      {loading && (
        <SkeletonAbsolute>
          <Skeleton label={label} />
        </SkeletonAbsolute>
      )}
      <TheIframe
        key={currentSrc}
        src={currentSrc}
        title="preview"
        onLoad={onLoad}
        onError={() => {
          clearTimeout(timerRef.current);
          if (fallbackSrc && !triedFallback) {
            setTriedFallback(true);
            setCurrentSrc(fallbackSrc);
            setLoading(true);
          } else {
            setLoading(false);
          }
        }}
        scrolling="yes"
        allowFullScreen
        frameBorder="0"
      />
    </IframeWrap>
  );
};

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];

const ImageViewer = ({ url, name }) => {
  const [zoomIndex, setZoomIndex] = useState(ZOOM_STEPS.indexOf(1));
  const [loading, setLoading] = useState(true);
  const zoom = ZOOM_STEPS[zoomIndex];

  return (
    <ImageArea>
      {loading && (
        <SkeletonAbsolute>
          <Skeleton label="Loading image…" />
        </SkeletonAbsolute>
      )}
      <ImageStage>
        <img
          src={url}
          alt={name}
          onLoad={() => setLoading(false)}
          onError={() => setLoading(false)}
          style={{
            maxWidth: zoom <= 1 ? "100%" : "none",
            maxHeight: zoom <= 1 ? "100%" : "none",
            width: zoom > 1 ? `${zoom * 100}%` : "auto",
            transform: zoom < 1 ? `scale(${zoom})` : "none",
            objectFit: "contain",
            borderRadius: 12,
            boxShadow: "0 8px 30px rgba(15, 23, 42, 0.12)",
            background: "#fff",
            transition: "all 0.2s ease",
          }}
        />
      </ImageStage>
      <ZoomBar>
        <ZoomBtn
          type="button"
          onClick={() => setZoomIndex((i) => Math.max(0, i - 1))}
          disabled={zoomIndex === 0}
          title="Zoom out"
          aria-label="Zoom out"
        >
          <ZoomOutIcon style={{ fontSize: 18 }} />
        </ZoomBtn>
        <ZoomValue
          type="button"
          onClick={() => setZoomIndex(ZOOM_STEPS.indexOf(1))}
          title="Reset zoom"
        >
          {Math.round(zoom * 100)}%
        </ZoomValue>
        <ZoomBtn
          type="button"
          onClick={() =>
            setZoomIndex((i) => Math.min(ZOOM_STEPS.length - 1, i + 1))
          }
          disabled={zoomIndex === ZOOM_STEPS.length - 1}
          title="Zoom in"
          aria-label="Zoom in"
        >
          <ZoomInIcon style={{ fontSize: 18 }} />
        </ZoomBtn>
      </ZoomBar>
    </ImageArea>
  );
};

const VideoViewer = ({ url, name }) => (
  <MediaViewerWrap>
    <VideoCard>
      <video
        src={url}
        controls
        style={{
          width: "100%",
          maxHeight: "65vh",
          borderRadius: 8,
          outline: "none",
        }}
        controlsList="nodownload"
      />
      <MediaTitle>{name}</MediaTitle>
    </VideoCard>
  </MediaViewerWrap>
);

const AudioViewer = ({ url, name }) => (
  <MediaViewerWrap>
    <AudioCard>
      <AudioDisc>{"🎵"}</AudioDisc>
      <AudioTitle>{name}</AudioTitle>
      <audio
        src={url}
        controls
        style={{ width: "100%", maxWidth: "340px" }}
        controlsList="nodownload"
      />
    </AudioCard>
  </MediaViewerWrap>
);

const EmailChipInput = ({
  value,
  onChange,
  suggestions,
  placeholder = "Type an email and press Enter",
}) => {
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const initializedRef = useRef(false);
  const inputRef = useRef(null);
  const suggestionList = Array.isArray(suggestions) ? suggestions : [];

  const emails = (value || "")
    .split(",")
    .map((e) => e.trim())
    .filter(Boolean);

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      if (!value && suggestionList.length) {
        onChange(suggestionList.join(", "));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const availableSuggestions = suggestionList.filter(
    (email) =>
      !emails.includes(email) &&
      (inputValue
        ? email.toLowerCase().includes(inputValue.toLowerCase())
        : true),
  );

  const addEmail = (raw) => {
    const email = raw.trim().replace(/,$/, "");
    if (!email) return;
    if (!EMAIL_REGEX.test(email)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    if (emails.includes(email)) {
      setInputValue("");
      return;
    }
    onChange([...emails, email].join(", "));
    setInputValue("");
  };

  const removeEmail = (email) => {
    onChange(emails.filter((e) => e !== email).join(", "));
  };

  const selectSuggestion = (email) => {
    if (emails.includes(email)) return;
    onChange([...emails, email].join(", "));
    setInputValue("");
    setInvalid(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
      if (inputValue.trim()) {
        e.preventDefault();
        addEmail(inputValue);
      }
    } else if (e.key === "Backspace" && !inputValue && emails.length) {
      removeEmail(emails[emails.length - 1]);
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) addEmail(inputValue);
    setTimeout(() => setShowDropdown(false), 150);
  };

  return (
    <ChipInputOuter>
      <InputAnchor>
        <ChipInputWrap
          $invalid={invalid}
          onClick={() => inputRef.current && inputRef.current.focus()}
        >
          {emails.map((email) => (
            <Chip key={email}>
              <ChipAvatar>{email.charAt(0).toUpperCase()}</ChipAvatar>
              <span title={email}>{email}</span>
              <ChipRemove
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeEmail(email);
                }}
                aria-label={`Remove ${email}`}
              >
                ×
              </ChipRemove>
            </Chip>
          ))}
          <ChipTextInput
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setShowDropdown(true);
              if (invalid) setInvalid(false);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowDropdown(true)}
            onBlur={handleBlur}
            placeholder={emails.length ? "Add more…" : placeholder}
          />
        </ChipInputWrap>

        {showDropdown && availableSuggestions.length > 0 && (
          <SuggestionDropdown>
            <SuggestionTitle>Suggestions</SuggestionTitle>
            {availableSuggestions.map((email) => (
              <SuggestionItem
                key={email}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectSuggestion(email)}
              >
                <ChipAvatar>{email.charAt(0).toUpperCase()}</ChipAvatar>
                <span>{email}</span>
              </SuggestionItem>
            ))}
          </SuggestionDropdown>
        )}
      </InputAnchor>

      {invalid ? (
        <FieldHint $error>Please enter a valid email address.</FieldHint>
      ) : (
        <FieldHint>
          Press Enter or comma to add. Backspace removes the last one.
        </FieldHint>
      )}
    </ChipInputOuter>
  );
};

const DocViewers = ({
  docview,
  setDocView,
  to = "",
  cc = "",
  inputs,
  values,
  handleChange,
  errors,
  handleSubmit,
  touched,
  setFieldValue,
  setFieldError,
  handleBlur,
  share = false,
  fileDownload = true,
  emailSuggestions = [],
}) => {
  const [activeTab, setActiveTab] = useState("preview");

  if (!docview?.state || !docview?.url) return null;

  const close = () => {
    setDocView({ state: false });
    setActiveTab("preview");
  };

  const fileName = docview.name || "Document";
  const raw = docview.url.split("?")[0];
  const ext = (
    docview.format?.toLowerCase() ||
    raw.split(".").pop()?.toLowerCase() ||
    ""
  ).trim();

  const encoded = encodeURIComponent(docview.url);
  const googleViewer = `https://docs.google.com/gview?url=${encoded}&embedded=true`;
  const officeViewer = `https://view.officeapps.live.com/op/embed.aspx?src=${encoded}`;

  const isDocx = ["docx", "doc", "pptx", "ppt"].includes(ext);
  const isPdf = ext === "pdf";
  const isTiff = ["tif", "tiff"].includes(ext);
  const isSheet = ["xlsx", "xls", "csv"].includes(ext);
  const isImage = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(
    ext,
  );
  const isText = ["txt", "xml", "json", "html", "htm", "md"].includes(ext);
  const isVideo = ["mp4", "mkv", "mov", "webm"].includes(ext);
  const isAudio = ["mp3", "wav", "ogg", "m4a"].includes(ext);

  const safeUrl =
    docview.url + (docview.url.includes("?") ? "&" : "?") + `_t=${Date.now()}`;
  const docs = [{ uri: safeUrl, fileName, fileType: ext }];

  const recipientCount = (to || "")
    .split(",")
    .map((e) => e.trim())
    .filter(Boolean).length;
  const ccCount = (values?.cc || cc || "")
    .split(",")
    .map((e) => e.trim())
    .filter(Boolean).length;

  return (
    <Modal open={docview.state} onClose={close} disableEnforceFocus>
      <Overlay>
        <ModalCard>
          {/* Header */}
          <TopBar>
            <LeftSide>
              <FileBadge $color={extColor[ext]}>
                <InsertDriveFileIcon style={{ fontSize: 20 }} />
                <BadgeExt>{(ext || "file").slice(0, 4)}</BadgeExt>
              </FileBadge>
              <DocNameWrapper>
                <DocName title={fileName}>{fileName}</DocName>
                <DocMeta>{getFileKind(ext)}</DocMeta>
              </DocNameWrapper>
            </LeftSide>

            {share && (
              <TabStrip role="tablist">
                <Tab
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "preview"}
                  $active={activeTab === "preview"}
                  onClick={() => setActiveTab("preview")}
                >
                  <VisibilityIcon style={{ fontSize: 16 }} />
                  Preview
                </Tab>
                <Tab
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "share"}
                  $active={activeTab === "share"}
                  onClick={() => setActiveTab("share")}
                >
                  <MailOutlineIcon style={{ fontSize: 16 }} />
                  Share
                </Tab>
              </TabStrip>
            )}

            <RightSide>
              {fileDownload && (
                <>
                  <IconAction
                    href={docview.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open in new tab"
                    aria-label="Open in new tab"
                  >
                    <OpenInNewIcon style={{ fontSize: 18 }} />
                  </IconAction>
                  <DownBtn
                    href={docview.url}
                    download={fileName}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Download document"
                  >
                    <GetAppIcon style={{ fontSize: 18 }} />
                    <span>Download</span>
                  </DownBtn>
                </>
              )}
              <CloseBtn
                type="button"
                onClick={close}
                title="Close (Esc)"
                aria-label="Close"
              >
                <CloseIcon style={{ fontSize: 18 }} />
              </CloseBtn>
            </RightSide>
          </TopBar>

          <ViewArea>
            {activeTab === "preview" && (
              <PreviewContainer>
                {isDocx && (
                  <IframeViewer
                    src={officeViewer}
                    fallbackSrc={googleViewer}
                    label="Loading document…"
                  />
                )}
                {isPdf && (
                  <IframeViewer
                    src={docview.url}
                    fallbackSrc={googleViewer}
                    label="Loading PDF…"
                  />
                )}
                {isSheet && (
                  <IframeViewer
                    src={officeViewer}
                    fallbackSrc={googleViewer}
                    label="Loading spreadsheet…"
                  />
                )}
                {isTiff && (
                  <TiffArea>
                    <TIFFViewer
                      tiff={safeUrl}
                      paginate="bottom"
                      zoomable
                      printable={false}
                    />
                  </TiffArea>
                )}
                {isImage && <ImageViewer url={docview.url} name={fileName} />}
                {isText && (
                  <IframeViewer src={docview.url} label="Loading file…" />
                )}
                {isVideo && <VideoViewer url={docview.url} name={fileName} />}
                {isAudio && <AudioViewer url={docview.url} name={fileName} />}

                {!isDocx &&
                  !isPdf &&
                  !isSheet &&
                  !isTiff &&
                  !isImage &&
                  !isText &&
                  !isVideo &&
                  !isAudio && (
                    <DocViewerWrap>
                      <DocViewer
                        documents={docs}
                        pluginRenderers={DocViewerRenderers}
                        config={{
                          header: { disableHeader: true },
                          loadingRenderer: {
                            overrideComponent: () => (
                              <Skeleton label="Loading…" />
                            ),
                          },
                        }}
                        style={{ width: "100%", height: "100%" }}
                      />
                    </DocViewerWrap>
                  )}
              </PreviewContainer>
            )}

            {activeTab === "share" && (
              <SharePanel>
                <ShareCard>
                  <ShareHeader>
                    <ShareIconWrapper>
                      <SendIcon style={{ fontSize: 22 }} />
                    </ShareIconWrapper>
                    <div>
                      <ShareTitle>Share Document</ShareTitle>
                      <SubHeading>
                        Send this document to others via email
                      </SubHeading>
                    </div>
                  </ShareHeader>

                  <AttachmentRow>
                    <AttachmentIcon $color={extColor[ext]}>
                      <InsertDriveFileIcon style={{ fontSize: 18 }} />
                    </AttachmentIcon>
                    <AttachmentMeta>
                      <AttachmentName title={fileName}>
                        {fileName}
                      </AttachmentName>
                      <AttachmentSub>
                        {getFileKind(ext)} · Attached
                      </AttachmentSub>
                    </AttachmentMeta>
                    <PreviewLink
                      type="button"
                      onClick={() => setActiveTab("preview")}
                    >
                      Preview
                    </PreviewLink>
                  </AttachmentRow>

                  <FormContainer>
                    {inputs.map((input, index) => (
                      <FormFieldWrapper key={index}>
                        {input.name === "cc" ? (
                          <>
                            <ChipFieldLabel>
                              {input.label}
                              {ccCount > 0 && <CountPill>{ccCount}</CountPill>}
                            </ChipFieldLabel>
                            <EmailChipInput
                              value={values.cc}
                              onChange={(val) => setFieldValue("cc", val)}
                              suggestions={emailSuggestions}
                            />
                            {touched.cc && errors.cc && (
                              <div className="error-text">{errors.cc}</div>
                            )}
                          </>
                        ) : (
                          <FormField
                            {...{
                              input,
                              values,
                              handleChange,
                              errors,
                              handleSubmit,
                              touched,
                              setFieldValue,
                              setFieldError,
                              handleBlur,
                            }}
                          />
                        )}
                      </FormFieldWrapper>
                    ))}
                  </FormContainer>

                  <ButtonContainer>
                    <PrimaryCTAButton
                      onClick={handleSubmit}
                      type="button"
                      disabled={!to.trim()}
                      fullWidth
                    >
                      <SendLabel>
                        <SendIcon style={{ fontSize: 16 }} />
                        {recipientCount > 1
                          ? `Send to ${recipientCount} recipients`
                          : "Send Document"}
                      </SendLabel>
                    </PrimaryCTAButton>

                    <HelperText>
                      {to.trim()
                        ? "The recipient will receive a secure link to view and download this document."
                        : "Add at least one recipient in the To field to send."}
                    </HelperText>
                  </ButtonContainer>
                </ShareCard>
              </SharePanel>
            )}
          </ViewArea>
        </ModalCard>
      </Overlay>
    </Modal>
  );
};

export default DocViewers;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(6px);
  animation: ${fadeIn} 0.3s ease-out;
  z-index: 1200;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const ModalCard = styled.div`
  width: 96vw;
  max-width: 1400px;
  height: 92vh;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow:
    0 24px 80px rgba(15, 23, 42, 0.25),
    0 0 1px rgba(15, 23, 42, 0.2);
  animation: ${slideUp} 0.4s cubic-bezier(0.16, 1, 0.3, 1);

  @media (max-width: 768px) {
    width: 100vw;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
  }
`;

const TopBar = styled.div`
  background: #ffffff;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid #eef0f4;
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    padding: 12px 14px;
    gap: 10px;
  }
`;

const LeftSide = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
`;

const FileBadge = styled.div`
  position: relative;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => p.$color || BRAND};
  background: ${(p) => `${p.$color || BRAND}14`};
`;

const BadgeExt = styled.span`
  position: absolute;
  bottom: -4px;
  right: -6px;
  padding: 1px 5px;
  border-radius: 6px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 9px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #374151;
`;

const DocNameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
`;

const DocName = styled.span`
  color: ${INK};
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.2px;
`;

const DocMeta = styled.span`
  color: #9ca3af;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
`;

const TabStrip = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  border-radius: 12px;
  background: #f4f6f8;
  flex-shrink: 0;

  @media (max-width: 768px) {
    order: 3;
    width: 100%;
  }
`;

const Tab = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 18px;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  ${(p) =>
    p.$active
      ? css`
          background: #ffffff;
          color: ${BRAND};
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.1);
        `
      : css`
          background: transparent;
          color: #6b7280;
          &:hover {
            color: ${INK};
            background: rgba(255, 255, 255, 0.6);
          }
        `}
  &:focus-visible {
    outline: 2px solid ${BRAND};
    outline-offset: 1px;
  }

  @media (max-width: 768px) {
    flex: 1;
  }
`;

const RightSide = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const iconButtonBase = css`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.18s ease;
  &:focus-visible {
    outline: 2px solid ${BRAND};
    outline-offset: 2px;
  }
`;

const IconAction = styled.a`
  ${iconButtonBase}
  color: #4b5563;
  background: #f4f6f8;
  text-decoration: none;
  &:hover {
    color: ${BRAND};
    background: ${BRAND}14;
  }
`;

const DownBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 16px;
  border-radius: 10px;
  color: #ffffff;
  background: ${BRAND};
  text-decoration: none;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  transition: all 0.18s ease;
  cursor: pointer;

  &:hover {
    background: ${BRAND_DARK};
    box-shadow: 0 6px 16px rgba(0, 131, 140, 0.3);
  }

  &:focus-visible {
    outline: 2px solid ${BRAND};
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    width: 38px;
    padding: 0;
    justify-content: center;

    span {
      display: none;
    }
  }
`;

const CloseBtn = styled.button`
  ${iconButtonBase}
  border: none;
  color: #6b7280;
  background: #f4f6f8;

  &:hover {
    background: #fee2e2;
    color: #ef4444;
    transform: rotate(90deg);
  }
`;

const ViewArea = styled.div`
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  background: #f1f5f9;
`;

const PreviewContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const IframeWrap = styled.div`
  position: relative;
  flex: 1 1 0;
  min-height: 0;
  width: 100%;
  height: 100%;
  background: #ffffff;
  border-radius: 0;
`;

const TheIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
  background: #ffffff;
  display: block;
`;

const TiffArea = styled.div`
  flex: 1 1 0;
  min-height: 0;
  overflow: auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 32px 24px;
  background: #ffffff;

  @media (max-width: 768px) {
    padding: 16px 12px;
  }
`;

const ImageArea = styled.div`
  position: relative;
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  background: #f1f5f9;
  background-image: radial-gradient(#e2e8f0 1px, transparent 1px);
  background-size: 18px 18px;
`;

const ImageStage = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 24px 80px;

  @media (max-width: 768px) {
    padding: 16px 12px 72px;
  }
`;

const ZoomBar = styled.div`
  position: absolute;
  left: 50%;
  bottom: 18px;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.15);
  border: 1px solid #e5e7eb;
  z-index: 6;
`;

const ZoomBtn = styled.button`
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 9px;
  background: transparent;
  color: #4b5563;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover:not(:disabled) {
    background: ${BRAND}14;
    color: ${BRAND};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ZoomValue = styled.button`
  min-width: 56px;
  height: 34px;
  border: none;
  border-radius: 9px;
  background: transparent;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  color: ${INK};
  cursor: pointer;
  &:hover {
    background: #f4f6f8;
  }
`;

const DocViewerWrap = styled.div`
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  width: 100%;
  height: 100%;
`;

const MediaViewerWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
`;

const VideoCard = styled.div`
  background: #0f172a;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  border: 1px solid #1e293b;
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MediaTitle = styled.div`
  color: #f1f5f9;
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AudioCard = styled.div`
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(8px);
  border-radius: 20px;
  padding: 36px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.4);
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
`;

const pulse = keyframes`
  0% { transform: scale(0.96); opacity: 0.8; }
  50% { transform: scale(1.04); opacity: 1; box-shadow: 0 8px 24px rgba(0, 129, 138, 0.25); }
  100% { transform: scale(0.96); opacity: 0.8; }
`;

const AudioDisc = styled.div`
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00818c, #00606a);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: #fff;
  box-shadow: 0 6px 16px rgba(0, 129, 138, 0.15);
  animation: ${pulse} 3s ease-in-out infinite;
`;

const AudioTitle = styled.div`
  color: #1e293b;
  font-size: 14px;
  font-weight: 600;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SharePanel = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 24px;
  background: #f1f5f9;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 16px 12px;
  }
`;

const ShareCard = styled.div`
  width: 100%;
  max-width: 560px;
  margin: auto 0;
  padding: 28px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
  animation: ${slideUp} 0.4s ease-out;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

const ShareHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
`;

const ShareIconWrapper = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #ffffff;
  background: linear-gradient(135deg, ${BRAND}, ${BRAND_DARK});
  box-shadow: 0 8px 20px rgba(0, 131, 140, 0.25);
`;

const ShareTitle = styled.h3`
  margin: 0;
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 17px;
  color: ${INK};
  letter-spacing: -0.3px;
`;

const SubHeading = styled.p`
  margin: 2px 0 0 0;
  color: #64748b;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13px;
`;

const AttachmentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  margin-bottom: 20px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #eef0f4;
`;

const AttachmentIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => p.$color || BRAND};
  background: ${(p) => `${p.$color || BRAND}14`};
`;

const AttachmentMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const AttachmentName = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: ${INK};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AttachmentSub = styled.div`
  margin-top: 1px;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  color: #9ca3af;
`;

const PreviewLink = styled.button`
  border: none;
  background: transparent;
  padding: 6px 10px;
  border-radius: 8px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  color: ${BRAND};
  cursor: pointer;
  flex-shrink: 0;
  &:hover {
    background: ${BRAND}14;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FormFieldWrapper = styled.div`
  animation: ${slideUp} 0.4s ease-out;

  &:nth-child(1) {
    animation-delay: 0.1s;
  }
  &:nth-child(2) {
    animation-delay: 0.15s;
  }
  &:nth-child(3) {
    animation-delay: 0.2s;
  }
`;

const SkeletonAbsolute = styled.div`
  position: absolute;
  inset: 0;
  background: #ffffff;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
`;

const SkeletonWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  animation: ${fadeIn} 0.4s ease-out;
`;

const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 3px solid #e2e8f0;
  border-top-color: ${BRAND};
  border-right-color: ${BRAND};
  animation: ${spin} 0.8s linear infinite;
  box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.1);
`;

const SkBars = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 260px;
`;

const SkBar = styled.div`
  height: 10px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 400px 100%;
  animation: ${shimmer} 1.5s ease infinite;
`;

const SkLabel = styled.p`
  color: #94a3b8;
  font-size: 13px;
  margin: 0;
  font-weight: 500;
  letter-spacing: 0.3px;
`;

const ButtonContainer = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const SendLabel = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const HelperText = styled.p`
  color: #64748b;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12.5px;
  text-align: center;
  line-height: 1.5;
  max-width: 420px;
  margin: 0;
`;

const ChipFieldLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  color: #6b7280;
  margin: 6px 0 8px;
`;

const CountPill = styled.span`
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  border-radius: 99px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${BRAND}14;
  color: ${BRAND};
  font-size: 11px;
`;

const ChipInputOuter = styled.div`
  position: relative;
  width: 100%;
`;

const InputAnchor = styled.div`
  position: relative;
`;

const ChipInputWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-height: 48px;
  padding: 6px 10px;
  border: 1.5px solid ${(p) => (p.$invalid ? "#ef4444" : "#e6e9f4")};
  border-radius: 10px;
  background: #ffffff;
  cursor: text;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:focus-within {
    border-color: ${(p) => (p.$invalid ? "#ef4444" : BRAND)};
    box-shadow: 0 0 0 3px
      ${(p) => (p.$invalid ? "rgba(239, 68, 68, 0.12)" : `${BRAND}1f`)};
  }
`;

const FieldHint = styled.div`
  margin-top: 6px;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 11.5px;
  color: ${(p) => (p.$error ? "#ef4444" : "#9ca3af")};
`;

const SuggestionDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  max-height: 220px;
  overflow-y: auto;
  padding: 4px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.12);
  z-index: 10;
  animation: ${fadeIn} 0.15s ease-out;
`;

const SuggestionTitle = styled.div`
  padding: 6px 10px 4px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 10.5px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #9ca3af;
`;

const SuggestionItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font-size: 13.5px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${INK};
  cursor: pointer;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    background: #eaf6f7;
    color: ${BRAND_DARK};
  }
`;

const ChipAvatar = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  background: ${BRAND};
  color: #ffffff;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 10px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #eaf6f7;
  color: ${BRAND_DARK};
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12.5px;
  padding: 3px 6px 3px 3px;
  border-radius: 20px;
  max-width: 100%;

  span:not(${ChipAvatar}) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 220px;
  }
`;

const ChipRemove = styled.button`
  border: none;
  background: rgba(0, 96, 106, 0.12);
  color: ${BRAND_DARK};
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: #fee2e2;
    color: #ef4444;
  }
`;

const ChipTextInput = styled.input`
  flex: 1;
  min-width: 140px;
  border: none;
  outline: none;
  font-size: 14px;
  padding: 6px 4px;
  background: transparent;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #1e293b;
`;
